'use client';
/** @jsxImportSource @emotion/react */

import { forwardRef } from 'react';

import type { RadioProps as MuiRadioProps } from '@mui/material/Radio';

import { buttonBaseClasses } from '@mui/material/ButtonBase';
import MuiRadio from '@mui/material/Radio';
import { useTheme } from '@mui/material/styles';

declare module '@mui/material/Radio' {
  interface RadioPropsColorOverrides {
    forceWhite: true;
  }
}
type ExcludedMuiProps =
  | 'checkedIcon'
  /**
   * The Radio component uses a <span> as its root element, which is essential for its styling and interactive behavior.
   * Changing the root element could disrupt its functionality, such as click handling and accessibility features.
   * Keeping the default element ensures proper behavior and layout.
   * And the default implementation in MUI is also broken, and altering it could lead to issues.
   *
   * https://serko.atlassian.net/browse/ZDS-1033
   */
  | 'component'
  | 'disableRipple'
  | 'icon';

type RadioProps = Omit<MuiRadioProps, ExcludedMuiProps>;

export type { RadioProps };
export const Radio = forwardRef<HTMLButtonElement, RadioProps>(
  ({ color, sx = [], ...props }, ref) => {
    const theme = useTheme();
    const resolvedColor =
      color === 'forceWhite' ? theme.palette.common.white : color;

    return (
      <MuiRadio
        {...props}
        color={color}
        sx={[
          ...(Array.isArray(sx) ? sx : [sx]),
          {
            [` &.${buttonBaseClasses.root}`]: {
              color: resolvedColor,
            },
          },
        ]}
        ref={ref}
      />
    );
  }
);

Radio.displayName = 'Radio';
